import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, useMediaQuery, FormControlLabel, Tooltip, Button } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import BulletList from './bullet-list';
import NumberList from './number-list';
import Model3DViewer from './model3DViewer';
import Callout from './callout';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import HandoutImageManager from './handout-image-manager';
import HandoutImageLoader from './handout-image-loader';
import IconStrip from './icon-strip';
import Copyright from './copyright';
import { GlobalStyles } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import AntSwitch from '../../UIControls/AntSwitch';
import CTAUpgradeDialog from '../../UIControls/CTAUpgradeDialog';
import ctaUpgradeProfessionalPaywall from '../../../assets/banners/ctaUpgradeProfessionalPaywall.png';
import Whiteboard from './whiteboard/Whiteboard';
import DisclaimerNotice from './disclaimer-notice';
import { useUnsavedChanges } from '../UnsavedChangesContext';
import EditableBlock from './customise-mode/EditableBlock';
import EditableList from './customise-mode/EditableList';
import { useCustomiseMode } from './customise-mode/CustomiseModeContext';
import WhiteboardSelectionModal from './customise-mode/WhiteboardSelectionModal';

function DisplayHandout({
    handoutData,
    showLabels,
    setShowLabels,
    show3DModel,
    showMainIllustration,
    showTutorialVideo,
    showWhiteboard,
    activeTool,
    color,
    thickness,
    canvasRef,
    onUnsavedChanges,
    setToolbarExpanded,
    showDisclaimer,
    doctorName,
    clinicName,
    onColorUsed,
    onCanvasModified, // Phase 1: Track canvas changes for combined mode
}) {
    const { authStore, imageStore, userDataStore } = useContext(StoreContext);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isSmallScreen = useMediaQuery('(max-width:750px)');
    const appBarHeight = theme.spacing(8);
    const locale = 'en-UK';
    const {
        isCustomiseMode,
        getCurrentContentData,
        isLoadedCustomisedHandout,
        setWhiteboardIllustration,
        selectedWhiteboard,
    } = useCustomiseMode();
    // const localeData = handoutData.contentData[locale];
    const rawLocaleData = getCurrentContentData(handoutData.contentData, locale);

    // Ensure all blocks have stable _reactKey from first render
    // IMPORTANT: Use the SAME key generation logic as CustomiseModeContext.ensureBlockKeys
    const localeData = useMemo(() => {
        if (!rawLocaleData) return rawLocaleData;

        return rawLocaleData.map((section) => {
            if (!section.blocks) return section;

            return {
                ...section,
                blocks: section.blocks.map((block, idx) => {
                    // If block already has _reactKey, keep it (from CustomiseMode)
                    if (block._reactKey) return block;

                    // Generate deterministic stable key - MUST MATCH CustomiseModeContext.ensureBlockKeys
                    const valueHash = block.value
                        ? String(block.value)
                              .substring(0, 30)
                              .replace(/[^a-zA-Z0-9]/g, '')
                        : block.type || 'block';
                    const stableKey = `block-${section.sectionName}-${idx}-${valueHash}`;

                    return {
                        ...block,
                        _reactKey: stableKey,
                    };
                }),
            };
        });
    }, [rawLocaleData]);

    // Only show icon if a saved customised handout was loaded from library
    const showCustomisedIcon = isLoadedCustomisedHandout;
    const is3DModel = localeData.every((section) => {
        return section.blocks.every((block) => block.type === '3dModel' || block.type === 'documentTitle');
    });

    const [upgradeCTAOpen, setUpgradeCTAOpen] = useState(false);
    const [whiteboardModalOpen, setWhiteboardModalOpen] = useState(false);
    let mainIllustrationRendered = false;
    const { hasUnsavedChanges, setHasUnsavedChanges, promptBeforeAction } = useUnsavedChanges();

    useEffect(() => {
        if (!showWhiteboard) return;

        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges, showWhiteboard]);

    useEffect(() => {
        if (
            is3DModel === true &&
            (userDataStore?.subscriptionData?.accountType === 'Free' ||
                userDataStore?.subscriptionData?.accountType === 'Student')
        ) {
            setUpgradeCTAOpen(true);
        }
    }, [is3DModel]);

    if (!authStore.idToken) {
        throw new Error(`No idToken found`);
    }

    if (!localeData) {
        throw new Error(`No data found for the specified locale: ${locale}`);
    }

    const handleToggleLabels = () => {
        const doToggle = () => setShowLabels((prev) => !prev);
        // If user has been drawing (unsaved canvas changes) and whiteboard is ON,
        // show the same confirmation dialog used for top-bar navigation.
        if (showWhiteboard && hasUnsavedChanges) {
            promptBeforeAction(doToggle);
        } else {
            doToggle();
        }
    };

    // Handler for whiteboard selection
    const handleWhiteboardSelect = (whiteboard) => {
        console.log('[Display Handout] Selected whiteboard:', whiteboard);
        setWhiteboardIllustration(whiteboard);
        setWhiteboardModalOpen(false);
    };

    return (
        <>
            <HandoutImageManager handoutData={handoutData}>
                <GlobalStyles
                    styles={{
                        '@media print': {
                            '#handout-content': {
                                paddingBottom: '100px',
                            },
                        },
                    }}
                />
                <Box id="handout-content" sx={{ my: 2, pb: isMobile ? appBarHeight : '0px', position: 'relative' }}>
                    {localeData.map((section, index) => generateSection(section, index))}
                    {showDisclaimer && <DisclaimerNotice doctorName={doctorName} clinicName={clinicName} />}
                    <Copyright />
                </Box>
            </HandoutImageManager>

            <CTAUpgradeDialog
                open={upgradeCTAOpen}
                handleClose={() => {
                    setUpgradeCTAOpen(false);
                }}
                onConfirm={() => navigate('/settings/manage-subscription')}
                title="Unlock saving, customising and more with just one click!"
                message="You've reached a feature that's beyond your current plan. Upgrade today to enjoy full access!"
                buttonText="Upgrade plan"
                benefits={[
                    'Save whiteboards and customised handouts',
                    'Replace illustrations with your own whiteboards',
                    'Unlimited digital sharing of all handouts & anatomy diagrams',
                ]}
                imageUrl={ctaUpgradeProfessionalPaywall}
                isLocked={false}
            />

            {/* Whiteboard Selection Modal */}
            <WhiteboardSelectionModal
                open={whiteboardModalOpen}
                onClose={() => setWhiteboardModalOpen(false)}
                onSelect={handleWhiteboardSelect}
                baseContentId={handoutData.contentId}
            />
        </>
    );

    function generateSection(jsonContent, index) {
        const sectionName = jsonContent.sectionName;
        const isPrintPageBreak = sectionName === 'printPageBreak';
        let blocks = jsonContent.blocks;

        // Filter blocks based on toggle states
        blocks = blocks.filter((block) => {
            if (
                (userDataStore?.subscriptionData?.accountType === 'Free' ||
                    userDataStore?.subscriptionData?.accountType === 'Student') &&
                block.type === '3dModel'
            ) {
                return false;
            }
            if (block.type === '3dModel' && !show3DModel) return false;
            if (block.type === 'illustration' && !showMainIllustration) return false;
            if (block.type === 'tutorialVideo' && !showTutorialVideo) return false;
            return true;
        });

        if (blocks.length === 0) {
            return null; // Skip rendering this section if no blocks remain
        }

        return (
            <React.Fragment key={index}>
                <Box
                    component="section"
                    className={`${sectionName}${isPrintPageBreak ? ' printPageBreak' : ''}`}
                    sx={{
                        my: 4,
                        '@media print': {
                            pageBreakBefore: isPrintPageBreak ? 'auto' : 'avoid',
                            position: 'relative',
                            zIndex: 1,
                        },
                    }}
                >
                    {sectionName === 'diagram' && blocks && blocks[0] && (
                        <FormControlLabel
                            control={<AntSwitch checked={showLabels} onChange={handleToggleLabels} />}
                            label="Labels"
                            labelPlacement="start"
                            sx={{
                                position: 'absolute',
                                top: isSmallScreen ? '-24px' : '16px',
                                right: 0,
                                '& .MuiFormControlLabel-label': { marginRight: '8px' },
                            }}
                        />
                    )}
                    {blocks.map((block, index) => (
                        <React.Fragment key={block._reactKey}>
                            {blockToComponentMapping(block, sectionName, index)}
                        </React.Fragment>
                    ))}
                </Box>
                {isPrintPageBreak && <div></div>}
            </React.Fragment>
        );
    }

    // top level component which checks CMS block type then generates MUI/custom component for different blocks
    function blockToComponentMapping(block, sectionName, index) {
        if (!block || !block.type) {
            console.error(`Block is missing type or is undefined`, block);
            return null;
        }
        switch (block.type) {
            case 'documentTitle':
                return (
                    <Box key={block.value} sx={{ position: 'relative' }}>
                        <Typography
                            component="h1"
                            variant="documentTitle"
                            sx={{
                                pr: showCustomisedIcon ? 6 : 0,
                            }}
                        >
                            {block.value}
                        </Typography>
                        {showCustomisedIcon && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: { xs: 4, sm: -4 },
                                    right: 0,
                                    px: { xs: 1.5, sm: 3 },
                                    py: { xs: 0.25, sm: 0.5 },
                                    backgroundColor: '#CCCCCC',
                                    borderRadius: 114, // approximate pill look
                                    pointerEvents: 'none',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: 'Inter',
                                        fontWeight: 400,
                                        fontSize: { xs: '0.85rem', sm: '1rem' },
                                        letterSpacing: '-0.5px',
                                        color: '#FFFFFF',
                                        textTransform: 'none',
                                    }}
                                >
                                    Edited
                                </Typography>
                            </Box>
                        )}
                    </Box>
                );
            case 'sectionTitle':
                return (
                    <Typography component="h2" key={block.value} variant="sectionTitle" sx={{ my: 2 }}>
                        {block.value}
                    </Typography>
                );
            case 'sectionSubtitle':
                return (
                    <Typography component="h3" key={block.value} variant="sectionSubtitle" sx={{ my: 2 }}>
                        {block.value}
                    </Typography>
                );
            case 'calloutSmall':
                return <Callout key={block.value}>{block.value}</Callout>;
            case 'calloutMedium':
                return <Callout key={block.value}>{block.value}</Callout>;
            case 'paragraph':
                // Wrap paragraph in EditableBlock if customise mode is active
                const paragraphComponent = (
                    <Typography
                        key={block.value}
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            pb: 1,
                            '@media print': {
                                pageBreakInside: 'auto',
                                pageBreakBefore: 'auto',
                                display: 'inline',
                            },
                        }}
                    >
                        {block.value}
                    </Typography>
                );

                // Wrap in EditableBlock to enable editing
                return (
                    <EditableBlock
                        key={block._reactKey || `${sectionName}-${index}`}
                        block={block}
                        sectionIndex={localeData.findIndex((s) => s.sectionName === sectionName)}
                        blockIndex={index}
                    >
                        {paragraphComponent}
                    </EditableBlock>
                );
            case 'list':
                const listComponent = <BulletList items={block.values} />;
                return (
                    <EditableList
                        key={block._reactKey || `${sectionName}-${index}`}
                        block={block}
                        sectionIndex={localeData.findIndex((s) => s.sectionName === sectionName)}
                        blockIndex={index}
                    >
                        {listComponent}
                    </EditableList>
                );
            case 'numberList':
                return <NumberList items={block.values} />;
            case 'iconStrip':
                return (
                    <IconStrip
                        iconDetails={block.iconDetails}
                        imageStore={imageStore}
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            '@media print': {
                                pageBreakInside: 'avoid',
                                pageBreakBefore: 'auto',
                                pb: 1,
                                display: 'flex',
                            },
                        }}
                    />
                );

            case 'illustration': {
                const isDiagramSection = sectionName === 'diagram';

                // If Diagram
                if (isDiagramSection) {
                    // Decide if *this* block is displayed based on showLabels
                    const isDisplayedBlock = index === (showLabels ? 0 : 1);
                    if (!isDisplayedBlock) {
                        // Skip rendering if it's the "other" block
                        return null;
                    }

                    // If it is the displayed block (this chnaged one), place the whiteboard on it
                    // Use stable key based on handout ID to prevent unmounting
                    const diagramKey = `illustration-${handoutData.contentId}-diagram`;
                    return (
                        <Box key={diagramKey} sx={{ position: 'relative', display: 'inline-block' }}>
                            <HandoutImageLoader
                                src={block.src}
                                alt={block.alt}
                                imageStore={imageStore}
                                id="main-illustration-img"
                            />
                            {showWhiteboard && (
                                <Box
                                    key={`whiteboard-wrapper-${handoutData.contentId}-diagram`}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                    }}
                                >
                                    <Whiteboard
                                        key={`whiteboard-${handoutData.contentId}-diagram`}
                                        backgroundImage={block.src}
                                        showWhiteboard={showWhiteboard}
                                        activeTool={activeTool}
                                        color={color}
                                        thickness={thickness}
                                        canvasRef={canvasRef}
                                        onUnsavedChanges={onUnsavedChanges}
                                        setToolbarExpanded={setToolbarExpanded}
                                        onColorUsed={onColorUsed}
                                        onCanvasModified={onCanvasModified}
                                    />
                                </Box>
                            )}
                        </Box>
                    );
                }

                // If NOT a Diagram section, show "Only the first illustration",
                if (!mainIllustrationRendered) {
                    // first illustration
                    mainIllustrationRendered = true; // toggle so the next illustration won't get Whiteboard
                    // Use stable key based on handout ID to prevent unmounting
                    const mainKey = `illustration-${handoutData.contentId}-main`;
                    return (
                        <Box key={mainKey} sx={{ position: 'relative', display: 'inline-block' }}>
                            <HandoutImageLoader
                                src={selectedWhiteboard?.canvasImageLink || block.src}
                                alt={block.alt}
                                imageStore={imageStore}
                                id="main-illustration-img"
                            />
                            {showWhiteboard && (
                                <Box
                                    key={`whiteboard-wrapper-${handoutData.contentId}-main`}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                    }}
                                >
                                    <Whiteboard
                                        key={`whiteboard-${handoutData.contentId}-main`}
                                        backgroundImage={selectedWhiteboard?.canvasImageLink || block.src}
                                        showWhiteboard={showWhiteboard}
                                        activeTool={activeTool}
                                        color={color}
                                        thickness={thickness}
                                        canvasRef={canvasRef}
                                        onUnsavedChanges={setHasUnsavedChanges}
                                        setToolbarExpanded={setToolbarExpanded}
                                        onColorUsed={onColorUsed}
                                        onCanvasModified={onCanvasModified}
                                    />
                                </Box>
                            )}
                            {isCustomiseMode && !showWhiteboard && (
                                <Button
                                    variant="contained"
                                    startIcon={<ImageIcon />}
                                    onClick={() => {
                                        // Check if user is on Free or Student plan - show upgrade dialog
                                        if (
                                            userDataStore?.subscriptionData?.accountType === 'Free' ||
                                            userDataStore?.subscriptionData?.accountType === 'Student'
                                        ) {
                                            setUpgradeCTAOpen(true);
                                            return;
                                        }
                                        setWhiteboardModalOpen(true);
                                    }}
                                    sx={{
                                        position: 'absolute',
                                        bottom: 16,
                                        right: 16,
                                        bgcolor: '#FF669B',
                                        '&:hover': { bgcolor: '#FF4D88' },
                                        textTransform: 'none',
                                    }}
                                >
                                    Replace Illustration
                                </Button>
                            )}
                        </Box>
                    );
                } else {
                    // normal handout: just an image
                    return <HandoutImageLoader src={block.src} alt={block.alt} imageStore={imageStore} />;
                }
            }
            case '3dModel': // 3D Model thing!
                return (
                    <Model3DViewer uid={block.uid} alt={block.alt} author={block.author} authorWeb={block.authorWeb} />
                );
            case 'tutorialVideo':
                return (
                    <Box key={block.src} sx={{ my: 2 }}>
                        <video width="100%" controls>
                            <source src={block.src} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    </Box>
                );
            case 'printPageBreak':
                return (
                    <>
                        <div className="printPageBreak"></div>
                        <p></p>
                    </>
                );

            default:
                return null;
        }
    }
}

export default DisplayHandout;
